/**
 * Composable pour gérer l'authentification et les erreurs de session
 * État partagé globalement (singleton)
 */

// État réactif partagé entre tous les composants
const showSessionExpiredModal = ref(false)
const sessionErrorMessage = ref('')

export const useAuthHandler = () => {
  /**
   * Gère les erreurs d'authentification des requêtes API
   */
  const handleAuthError = (error: any) => {
    // Vérifier si c'est une erreur 401
    if (error?.statusCode === 401 || error?.response?.status === 401) {
      const errorData = error?.data || error?.response?.data

      // Déterminer le message à afficher
      if (errorData?.code === 'TOKEN_EXPIRED') {
        sessionErrorMessage.value = 'Votre session a expiré.'
      } else if (errorData?.code === 'INVALID_TOKEN' || errorData?.code === 'MALFORMED_TOKEN') {
        sessionErrorMessage.value = 'Votre session est invalide.'
      } else if (errorData?.code === 'NO_TOKEN') {
        sessionErrorMessage.value = 'Vous devez être connecté.'
      } else {
        sessionErrorMessage.value = errorData?.message || 'Votre session a expiré.'
      }

      // Nettoyer le token
      if (process.client) {
        sessionStorage.removeItem('tokenAdmin')
      }

      // Afficher la modale
      showSessionExpiredModal.value = true

      return true
    }

    return false
  }

  /**
   * Ferme la modale et redirige vers la page de connexion
   */
  const handleReconnect = () => {
    showSessionExpiredModal.value = false
    
    if (process.client) {
      // Rediriger vers la page de login
      window.location.href = 'auth/login'
    }
  }

  /**
   * Wrapper pour les appels API avec gestion automatique des erreurs d'auth
   */
  const withAuthErrorHandling = async <T>(apiCall: () => Promise<T>): Promise<T | null> => {
    try {
      return await apiCall()
    } catch (error) {
      if (handleAuthError(error)) {
        // Erreur d'authentification gérée
        return null
      }
      // Relancer l'erreur si ce n'est pas une erreur d'auth
      throw error
    }
  }

  return {
    showSessionExpiredModal,
    sessionErrorMessage,
    handleAuthError,
    handleReconnect,
    withAuthErrorHandling
  }
}
